import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Save, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/apiBase';

const LANES = [
  { value: 'build_next', label: '🚀 Build Next', hint: 'Revenue opportunity' },
  { value: 'fix_now', label: '⚠️ Fix Now', hint: 'Churn risk or blocker' },
  { value: 'ignore', label: '❌ Ignore', hint: 'Low priority / noise' },
];

const EditDecisionModal = ({ open, onOpenChange, decision, onSaved }) => {
  const [form, setForm] = useState({
    decision_lane: 'build_next',
    summary: '',
    why: '',
    suggested_next_step: '',
    expected_outcome: '',
    urgency: 3,
    revenue_impact: 3,
    churn_risk: 1,
    effort_hours: 8,
    signal_text: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (decision && open) {
      setForm({
        decision_lane: decision.decision_lane || 'build_next',
        summary: decision.summary || '',
        why: decision.why || '',
        suggested_next_step: decision.suggested_next_step || '',
        expected_outcome: decision.expected_outcome || '',
        urgency: decision.urgency ?? 3,
        revenue_impact: decision.revenue_impact ?? 3,
        churn_risk: decision.churn_risk ?? 1,
        effort_hours: decision.effort_hours ?? 8,
        signal_text: decision.signal?.text || '',
      });
    }
  }, [decision, open]);

  const setField = (key, val) => setForm((s) => ({ ...s, [key]: val }));

  const handleSave = async () => {
    if (!decision) return;
    setSaving(true);
    try {
      const payload = {
        decision_lane: form.decision_lane,
        summary: form.summary.trim(),
        why: form.why.trim(),
        suggested_next_step: form.suggested_next_step.trim(),
        expected_outcome: form.expected_outcome.trim(),
        urgency: Number(form.urgency),
        revenue_impact: Number(form.revenue_impact),
        churn_risk: Number(form.churn_risk),
        effort_hours: Number(form.effort_hours),
        signal_text: form.signal_text.trim(),
      };
      const res = await axios.put(`${API_URL}/api/decisions/${decision.signal_id}`, payload);
      toast.success('Decision updated');
      onOpenChange(false);
      onSaved?.(res.data.decision);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[560px] overflow-y-auto" data-testid="edit-decision-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Edit decision
          </SheetTitle>
          <SheetDescription>
            Override the AI's categorization, reasoning, and estimates. Your edits will replace the AI's original assessment.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-6">
          {/* Lane */}
          <div className="space-y-2">
            <Label>Lane</Label>
            <Select value={form.decision_lane} onValueChange={(v) => setField('decision_lane', v)}>
              <SelectTrigger data-testid="edit-decision-lane">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    <div className="flex items-center justify-between gap-3">
                      <span>{l.label}</span>
                      <span className="text-xs text-muted-foreground">{l.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Signal text */}
          <div className="space-y-2">
            <Label>Original signal text</Label>
            <Textarea
              value={form.signal_text}
              onChange={(e) => setField('signal_text', e.target.value)}
              rows={2}
              maxLength={5000}
              data-testid="edit-decision-signal-text"
            />
          </div>

          {/* Summary */}
          <div className="space-y-2">
            <Label>Summary</Label>
            <Input
              value={form.summary}
              onChange={(e) => setField('summary', e.target.value)}
              maxLength={200}
              data-testid="edit-decision-summary"
            />
          </div>

          {/* Why */}
          <div className="space-y-2">
            <Label>Why this matters</Label>
            <Textarea
              value={form.why}
              onChange={(e) => setField('why', e.target.value)}
              rows={3}
              maxLength={2000}
              data-testid="edit-decision-why"
            />
          </div>

          {/* Suggested next step */}
          <div className="space-y-2">
            <Label>Suggested next step</Label>
            <Textarea
              value={form.suggested_next_step}
              onChange={(e) => setField('suggested_next_step', e.target.value)}
              rows={2}
              maxLength={2000}
              data-testid="edit-decision-next-step"
            />
          </div>

          {/* Expected outcome */}
          <div className="space-y-2">
            <Label>Expected outcome</Label>
            <Textarea
              value={form.expected_outcome}
              onChange={(e) => setField('expected_outcome', e.target.value)}
              rows={2}
              maxLength={2000}
              data-testid="edit-decision-outcome"
            />
          </div>

          {/* Numeric scores */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Urgency <Badge variant="outline" className="ml-1 text-[10px]">1–5</Badge></Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.urgency}
                onChange={(e) => setField('urgency', e.target.value)}
                data-testid="edit-decision-urgency"
              />
            </div>
            <div className="space-y-2">
              <Label>Revenue impact <Badge variant="outline" className="ml-1 text-[10px]">1–5</Badge></Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.revenue_impact}
                onChange={(e) => setField('revenue_impact', e.target.value)}
                data-testid="edit-decision-revenue"
              />
            </div>
            <div className="space-y-2">
              <Label>Churn risk <Badge variant="outline" className="ml-1 text-[10px]">1–5</Badge></Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.churn_risk}
                onChange={(e) => setField('churn_risk', e.target.value)}
                data-testid="edit-decision-churn"
              />
            </div>
            <div className="space-y-2">
              <Label>Effort (hours)</Label>
              <Input
                type="number"
                min={0.5}
                max={200}
                step={0.5}
                value={form.effort_hours}
                onChange={(e) => setField('effort_hours', e.target.value)}
                data-testid="edit-decision-effort"
              />
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 border p-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            ROI/hour will be automatically recalculated when you save.
          </div>
        </div>

        <SheetFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="btn-cta" disabled={saving || !form.summary.trim()} data-testid="edit-decision-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-2" />Save decision</>}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EditDecisionModal;
